import React, { useState, useEffect } from 'react'
import { Modal, FormGroup, FormControl, Button, Radio } from 'react-bootstrap'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import ReactSelect, { Option } from 'react-select'

import Label from 'live/components/forms/ui/label'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { AbsoluteLoading, RoundedModal } from 'plugin-channelmanager/generic'
import { createChannel } from 'plugin-channelmanager/services'
import { listChannelTypes } from 'plugin-channelmanager/services/channelTypes'

import { AddChannelModalBody } from './styles'

interface IProps {
    show: boolean
    handleClose: () => void
    handleSave: () => void
}

interface IChannelCreationForm extends Omit<IChannel, 'channelType'> {
    idType: 'manual' | 'automatic'
    channelType: Option<number>
}

const formDefault: IChannelCreationForm = {
    name: '',
    id: '',
    channelType: { value: null, label: '' },
    idType: 'automatic'
}

export const AddChannelModal: React.FC<IProps> = ({ show, handleClose, handleSave }) => {
    const { handleSubmit, control, formState, setValue, watch, reset } = useForm<IChannelCreationForm>({
        mode: 'all',
        defaultValues: formDefault
    })

    const [channelTypes, setChannelTypes] = useState<Option<string>>([])
    const [isLoading, setIsLoading] = useState(true)

    const handleCloseProtocol = (fromCreation?: boolean): void => {
        reset()

        if (fromCreation) {
            handleSave()
        } else {
            handleClose()
        }
    }

    const handleCreate: SubmitHandler<IChannelCreationForm> = async (data): Promise<void> => {
        setIsLoading(true)
        try {
            const postData = await createChannel({
                ...data,
                channelTypeId: data.channelType.value
            })

            handleCloseProtocol(true)
            globalNotification.success(i18n('Channel created successfully.'))
        } catch (err) {
            globalNotification.error(i18n(err.message) ?? i18n('An error occurred on creating channel.'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleListChannelTypes = async (): Promise<void> => {
        setIsLoading(true)
        try {
            const response = await listChannelTypes()

            setChannelTypes(response.map(op => ({ value: op.id, label: i18n(op.name) })))
            setIsLoading(false)
        } catch (error) {
            globalNotification.error(i18n(error.message) ?? i18n('An error ocurred on listing channel types'))

            return handleListChannelTypes()
        }
    }

    useEffect(() => {
        handleListChannelTypes()
    }, [])

    return (
        <RoundedModal show={show} onHide={() => handleCloseProtocol()} aria-labelledby="contained-modal-title">
            <form onSubmit={handleSubmit(handleCreate)}>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title">{i18n('New Channel')}</Modal.Title>
                </Modal.Header>
                <AddChannelModalBody>
                    <Controller
                        rules={{ required: true }}
                        name="name"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('Channel name')}</Label>
                                <FormControl
                                    type="text"
                                    id="channelsList-createModal-field-name"
                                    ref={field.ref}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    maxLength={255}
                                />
                            </FormGroup>
                        )}
                    />

                    <Controller
                        rules={{ required: true }}
                        name="channelType"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('Type')}</Label>
                                <ReactSelect
                                    options={channelTypes}
                                    id="channelsList-createModal-field-type"
                                    onChange={(obj: Option<number>) => field.onChange(obj)}
                                    value={field.value}
                                    searchable={true}
                                    onBlur={field.onBlur}
                                    clearable={false}
                                    placeholder={`${i18n('Select')}...`}
                                />
                            </FormGroup>
                        )}
                    />

                    <Controller
                        rules={{ required: true }}
                        name="id"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('ID')}</Label>
                                <FormControl
                                    type="text"
                                    id="channelsList-createModal-field-id"
                                    ref={field.ref}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    maxLength={255}
                                    // disabled={idType === 'automatic'}
                                />
                            </FormGroup>
                        )}
                    />
                </AddChannelModalBody>
                <Modal.Footer>
                    <Button
                        className="no-outline-button"
                        bsStyle="primary"
                        type="submit"
                        id="diagramList-createModal-button-create"
                        disabled={!formState.isValid}
                    >
                        {i18n('Create')}
                    </Button>
                    <Button
                        className="no-outline-button"
                        type="button"
                        id="diagramList-createModal-button-close"
                        onClick={() => handleCloseProtocol()}
                    >
                        {i18n('Close')}
                    </Button>
                </Modal.Footer>
            </form>
            <AbsoluteLoading show={isLoading} />
        </RoundedModal>
    )
}
